import { GetDeviceSnapshotDTO, UnitStateV2 } from '@eppendorf/vnls-device-service-utils';
import { Sidecar, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { ReactElement, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { BatteryStatus } from '$components/device/battery-status';
import { SignalStrengthIcon } from '$components/device/signal-strength-icon';
import { LoadingErrorHint } from '$components/loading-error-hint/loading-error-hint';
import { SidecarProvider } from '$components/sidecar/sidecar-context';
import { SidecarRouterOutlet } from '$components/sidecar-router/sidecar-router-outlet';
import { TabsNavigationSidecar } from '$components/tabs';
import { DeviceDetailHeader } from '$features/devices/device-detail-header';
import { DeviceDetailRetryReg } from '$features/devices/device-detail-retry-reg/device-detail-retry-reg';
import { DeviceStatusSnapshot } from '$features/devices/device-status/device-status';
import { useGetDevice } from '$features/devices/devices.api';
import { useDeviceViewConfig } from '$features/devices/devices.view.config';
import { useDeviceSnapshot } from '$features/monitoring/device-snapshot.api';

function getBatteryFromSnapshot(snapshot?: GetDeviceSnapshotDTO) {
  const batterySocComponent = snapshot?.currentShadow.components.find(
    (c) => c.id === 'batterySoc',
  );
  const batterySoc = Number(batterySocComponent?.state);
  const batterySocNum = Number.isNaN(batterySoc) ? undefined : batterySoc;
  const batteryChargingComponent = snapshot?.currentShadow.components.find(
    (c) => c.id === 'batteryCharging',
  );
  const batteryCharging = Boolean(batteryChargingComponent?.state);

  return {
    soc: batterySocNum,
    charging: batteryCharging,
  };
}

function getSignalStrengthFromSnapshot(snapshot?: GetDeviceSnapshotDTO) {
  const signalStrengthComponent = snapshot?.currentShadow.components.find(
    (c) => c.id === 'signalStrength',
  );
  const signalStrength = Number(signalStrengthComponent?.state);
  return Number.isNaN(signalStrength) ? undefined : signalStrength;
}

export function DeviceDetail(): ReactElement {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isSidecardClosing, setIsSidecarClosing] = useState(false);
  const [searchParams] = useSearchParams();

  const { data: deviceData, isError } = useGetDevice({
    manufacturer: searchParams.get('manufacturer') ?? '',
    serialNumber: searchParams.get('serialNumber') ?? '',
  });

  const [isSidecarOpen, setIsSidecarOpen] = useState(true);
  const navigate = useNavigate();

  const viewConfig = useDeviceViewConfig(deviceData);

  const { data: snapshot, isLoading: isLoadingSnapshot } = useDeviceSnapshot({
    manufacturer: searchParams.get('manufacturer')!,
    serialNumber: searchParams.get('serialNumber')!,
  });

  const batteryStatus = getBatteryFromSnapshot(snapshot);

  const signalStrength = getSignalStrengthFromSnapshot(snapshot);

  const {
    formState: { isDirty: isFormDirty },
  } = useFormContext();

  function handleSidecarOpenChange(): void {
    setIsSidecarClosing(true);

    if (isFormDirty) {
      setIsCancelDialogOpen(true);
      return;
    }

    setIsSidecarOpen(false);
    navigate({
      search: '',
    });
  }

  function handleOnTabChange(): void {
    setIsCancelDialogOpen(true);
  }

  const inventoryDevice = snapshot?.inventoryDevice;

  return (
    <SidecarProvider
      value={{ isCancelDialogOpen, setIsCancelDialogOpen, isSidecardClosing }}
    >
      <Sidecar isOpen={isSidecarOpen} onOpenChange={() => handleSidecarOpenChange()}>
        <div className="sidecar__layout">
          <div className="sidecar__masthead">
            <DeviceDetailHeader
              className="sidecar__device-detail-header"
              device={inventoryDevice}
            />
            <div className="sidecar__device-states-group">
              <div className="sidecar__device-state">
                {isLoadingSnapshot ? (
                  <div className="realtime-cell__loading" />
                ) : (
                  <DeviceStatusSnapshot bordered deviceSnapshot={snapshot} />
                )}
              </div>
              {viewConfig.showBattery &&
                snapshot &&
                snapshot.currentShadow.activityState !== UnitStateV2.Offline && (
                  <BatteryStatus
                    soc={batteryStatus?.soc}
                    charging={batteryStatus?.charging}
                    className="sidecar__device-battery-state"
                  />
                )}

              {viewConfig.showSignalStrength &&
                snapshot &&
                snapshot.currentShadow.activityState !== UnitStateV2.Offline && (
                  <div className="sidecar__device-signal-strength">
                    <SignalStrengthIcon
                      signalStrength={signalStrength ?? null}
                      size={IconSizeClasses.Responsive}
                      className="icon--signal-strength"
                    />
                  </div>
                )}
            </div>
          </div>
          {isError && <LoadingErrorHint className="m-top-xl" />}
          {inventoryDevice && (
            <>
              <DeviceDetailRetryReg device={inventoryDevice} />
              <div className="sidecar__details box box-remove-padding">
                <div className="sidecar__tabs-navigation">
                  <TabsNavigationSidecar
                    routes={viewConfig.deviceDetailSidecarRoutes}
                    onTabChange={isFormDirty ? handleOnTabChange : undefined}
                    shouldChangeTabOnActive={!isFormDirty}
                  />
                </div>
                <div className="sidecar__outlet-wrapper">
                  <SidecarRouterOutlet />
                </div>
              </div>
            </>
          )}
        </div>
      </Sidecar>
    </SidecarProvider>
  );
}
